import { AdminOrganization, AdminUser } from "../../utils/types";
import type { ComponentType } from "react";
import {
  IconActivityHeartbeat,
  IconChartBar,
  IconCloudUpload,
  IconUsers,
} from "@tabler/icons-react";
import { formatAppDateTime, parseApiDateTime } from "../../utils/date";
import { buildAdminStats } from "./adminStats";

const formatPercent = (value: number) => `${value}%`;
const safeRatio = (part: number, total: number) =>
  total === 0 ? 0 : Math.round((part / total) * 100);
const formatDateTime = (value: string | null, emptyLabel: string) => {
  if (!value) return emptyLabel;
  if (Number.isNaN(parseApiDateTime(value).getTime())) return "Unknown";
  return formatAppDateTime(value);
};

type Props = {
  users: AdminUser[];
  organizations: AdminOrganization[];
};

export const AdminStatisticsPanel = ({ users, organizations }: Props) => {
  const stats = buildAdminStats(users, organizations);

  return (
    <section className="flex flex-col gap-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <header>
        <h2 className="text-xl font-semibold text-slate-900">
          User Statistics & Analytics
        </h2>
        <p className="text-sm text-slate-600">
          Monitor user engagement, role distribution, and account activity.
        </p>
      </header>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Total users"
          value={String(stats.totalUsers)}
          helper="Across all organizations"
          Icon={IconUsers}
        />
        <StatCard
          label="Active users (30 days)"
          value={String(
            stats.loginBuckets["24h"] +
              stats.loginBuckets["7d"] +
              stats.loginBuckets["30d"],
          )}
          helper={`${safeRatio(
            stats.loginBuckets["24h"] +
              stats.loginBuckets["7d"] +
              stats.loginBuckets["30d"],
            stats.totalUsers,
          )}% engagement`}
          Icon={IconActivityHeartbeat}
        />
        <StatCard
          label="Onboarding completion"
          value={formatPercent(safeRatio(stats.linkedUsers, stats.totalUsers))}
          helper={`${stats.unlinkedUsers} users not linked`}
          Icon={IconChartBar}
        />
        <StatCard
          label="Upload adoption"
          value={formatPercent(stats.uploadAdoptionPct)}
          helper={`${stats.uploadedUsers} users uploaded`}
          Icon={IconCloudUpload}
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <div className="rounded-xl border border-slate-200 p-4">
          <h3 className="text-sm font-semibold text-slate-900">
            User activity buckets
          </h3>
          <div className="mt-3 space-y-3">
            <MetricBar
              label="Last 24h"
              count={stats.loginBuckets["24h"]}
              total={stats.totalUsers}
              tone="bg-emerald-500"
            />
            <MetricBar
              label="Last 7d"
              count={stats.loginBuckets["7d"]}
              total={stats.totalUsers}
              tone="bg-sky-500"
            />
            <MetricBar
              label="Last 30d"
              count={stats.loginBuckets["30d"]}
              total={stats.totalUsers}
              tone="bg-indigo-500"
            />
            <MetricBar
              label="Inactive / Unknown"
              count={stats.loginBuckets.inactive}
              total={stats.totalUsers}
              tone="bg-slate-500"
            />
          </div>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <div className="rounded-xl border border-slate-200 p-4">
          <h3 className="text-sm font-semibold text-slate-900">Newest users</h3>
          <div className="mt-3 space-y-2">
            {stats.recentSignups.map((item) => (
              <div
                key={`signup-${item.id}`}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2"
              >
                <p className="text-sm font-medium text-slate-900">
                  {item.username}
                </p>
                <p className="text-xs text-slate-500">{item.email}</p>
                <p className="mt-1 text-xs text-slate-700">
                  Joined: {formatDateTime(item.createdAt, "Unknown")}
                </p>
              </div>
            ))}
            {stats.recentSignups.length === 0 && (
              <p className="text-sm text-slate-500">No users found.</p>
            )}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 p-4">
          <h3 className="text-sm font-semibold text-slate-900">
            Recently active
          </h3>
          <div className="mt-3 space-y-2">
            {stats.recentActivity.map((item) => (
              <div
                key={item.id}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2"
              >
                <p className="text-sm font-medium text-slate-900">
                  {item.username}
                </p>
                <p className="text-xs text-slate-500">{item.email}</p>
                <p className="mt-1 text-xs text-slate-700">
                  Last login:{" "}
                  {formatDateTime(item.lastLoggedIn, "No login data")}
                </p>
              </div>
            ))}
            {stats.recentActivity.length === 0 && (
              <p className="text-sm text-slate-500">
                No recent activity found.
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 p-4">
        <h3 className="text-sm font-semibold text-slate-900">
          Organization membership
        </h3>
        <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {stats.orgDistribution.slice(0, 6).map((entry) => (
            <div
              key={entry.orgId === null ? "unlinked" : String(entry.orgId)}
              className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
            >
              <p className="text-xs text-slate-500">{entry.orgName}</p>
              <p className="text-sm font-semibold text-slate-900">
                {entry.count} users
              </p>
            </div>
          ))}
          {stats.orgDistribution.length === 0 && (
            <p className="text-sm text-slate-500">
              No organization distribution found.
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

const StatCard = ({
  label,
  value,
  helper,
  Icon,
}: {
  label: string;
  value: string;
  helper: string;
  Icon: ComponentType<{ size?: number; className?: string }>;
}) => (
  <article className="rounded-xl border border-slate-200 bg-slate-50 p-4">
    <div className="flex items-center justify-between">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
        {label}
      </p>
      <Icon size={16} className="text-slate-500" />
    </div>
    <p className="mt-2 text-2xl font-semibold text-slate-900">{value}</p>
    <p className="mt-1 text-xs text-slate-500">{helper}</p>
  </article>
);

const MetricBar = ({
  label,
  count,
  total,
  tone,
}: {
  label: string;
  count: number;
  total: number;
  tone: string;
}) => {
  const width = safeRatio(count, total);
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-xs text-slate-700">
        <span>{label}</span>
        <span className="font-semibold">{count}</span>
      </div>
      <div className="h-2 rounded-full bg-slate-100">
        <div
          className={`h-2 rounded-full ${tone}`}
          style={{ width: `${Math.max(width, count > 0 ? 8 : 0)}%` }}
        />
      </div>
    </div>
  );
};
